import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useActualizarFoto,
  useEliminarFoto,
  useFotosMuestra,
  useSubirFoto,
} from '@/hooks/useLaboratorio';
import { comprimirImagen } from '@/lib/comprimir-imagen';
import { cn } from '@/lib/utils';
import type { FotoMuestra } from '@adelante/shared';
import { ImageOff, Loader2, Trash2, Upload } from 'lucide-react';
import { useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

/**
 * Galería de fotos de una muestra de laboratorio.
 *
 * - Subida con COMPRESIÓN en el navegador (~150–300 KB).
 * - Cada foto se puede ASIGNAR a un ensayo (edad: 7d / 14d / 28d…) o quedar
 *   como "General" de la muestra.
 * - Filtro por ensayo en la galería + reasignación desde el visor.
 *
 * Las imágenes se sirven con URLs SAS temporales generadas por el backend.
 */

const SENTINEL_GENERAL = 'general';

type AsignacionUI = typeof SENTINEL_GENERAL | string; // string = id_ensayo

interface EnsayoLite {
  id: number | string;
  edad_dias: number;
}

interface Props {
  idMuestra: number | string;
  ensayos: EnsayoLite[];
  puedeEscribir: boolean;
}

/** Convierte el valor del Select (string) al id_ensayo del backend (number | null). */
function asignacionAEnsayo(a: AsignacionUI): number | null {
  return a === SENTINEL_GENERAL ? null : Number(a);
}

/** Valor del Select correspondiente al id_ensayo de una foto. */
function ensayoAAsignacion(idEnsayo: number | null): AsignacionUI {
  return idEnsayo === null ? SENTINEL_GENERAL : String(idEnsayo);
}

export function GaleriaFotosMuestra({ idMuestra, ensayos, puedeEscribir }: Props) {
  const consulta = useFotosMuestra(idMuestra);
  const subir = useSubirFoto();
  const eliminar = useEliminarFoto();
  const actualizar = useActualizarFoto();

  const fotos = consulta.data?.data?.fotos ?? [];
  const errorBackend = consulta.data?.error?.mensaje ?? null;

  const ensayosOrden = useMemo(
    () => [...ensayos].sort((a, b) => a.edad_dias - b.edad_dias),
    [ensayos],
  );

  // Map id_ensayo → edad_dias para mostrar el badge sin buscar cada vez.
  const labelEnsayo = useMemo(() => {
    const m = new Map<number, string>();
    for (const e of ensayosOrden) m.set(Number(e.id), `${e.edad_dias}d`);
    return m;
  }, [ensayosOrden]);

  const inputRef = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState(0);
  const [verFoto, setVerFoto] = useState<FotoMuestra | null>(null);
  const [asignarA, setAsignarA] = useState<AsignacionUI>(SENTINEL_GENERAL);
  const [filtro, setFiltro] = useState<AsignacionUI | 'todas'>('todas');

  // Conteos por filtro.
  const conteos = useMemo(() => {
    const c = { todas: fotos.length, general: 0, porEnsayo: new Map<number, number>() };
    for (const f of fotos) {
      if (f.id_ensayo === null) c.general++;
      else c.porEnsayo.set(f.id_ensayo, (c.porEnsayo.get(f.id_ensayo) ?? 0) + 1);
    }
    return c;
  }, [fotos]);

  // Fotos filtradas según el chip activo.
  const fotosFiltradas = useMemo(() => {
    if (filtro === 'todas') return fotos;
    if (filtro === SENTINEL_GENERAL) return fotos.filter((f) => f.id_ensayo === null);
    const n = Number(filtro);
    return fotos.filter((f) => f.id_ensayo === n);
  }, [fotos, filtro]);

  const onArchivos = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter((f) => f.type.startsWith('image/'));
    if (arr.length === 0) {
      toast.warning('Seleccioná archivos de imagen.');
      return;
    }
    setSubiendo(arr.length);
    let ok = 0;
    const idEnsayo = asignacionAEnsayo(asignarA);
    for (const f of arr) {
      try {
        const blob = await comprimirImagen(f);
        const r = await subir.mutateAsync({ idMuestra, blob, nombre: f.name, idEnsayo });
        if (r.data) ok++;
      } catch {
        // el hook ya muestra el toast de error
      } finally {
        setSubiendo((n) => Math.max(0, n - 1));
      }
    }
    setSubiendo(0);
    if (ok > 0) toast.success(`${ok} foto${ok === 1 ? '' : 's'} agregada${ok === 1 ? '' : 's'}`);
    if (inputRef.current) inputRef.current.value = '';
  };

  const chips: Array<{ valor: AsignacionUI | 'todas'; label: string; count: number }> = [
    { valor: 'todas', label: 'Todas', count: conteos.todas },
    { valor: SENTINEL_GENERAL, label: 'General', count: conteos.general },
    ...ensayosOrden.map((e) => ({
      valor: String(e.id) as AsignacionUI,
      label: `${e.edad_dias}d`,
      count: conteos.porEnsayo.get(Number(e.id)) ?? 0,
    })),
  ];

  return (
    <div className="space-y-3">
      {/* Barra superior: contador + (si puede escribir) selector de asignación + botón */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-muted-foreground">
          {fotos.length} foto{fotos.length === 1 ? '' : 's'}
        </div>
        {puedeEscribir && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-muted-foreground">Asignar a:</span>
            <Select value={asignarA} onValueChange={(v) => setAsignarA(v as AsignacionUI)}>
              <SelectTrigger className="h-8 w-[140px] text-xs">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SENTINEL_GENERAL}>General</SelectItem>
                {ensayosOrden.map((e) => (
                  <SelectItem key={String(e.id)} value={String(e.id)}>
                    {e.edad_dias} días
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <input
              ref={inputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => onArchivos(e.target.files)}
            />
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5"
              disabled={subiendo > 0}
              onClick={() => inputRef.current?.click()}
            >
              {subiendo > 0 ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> Subiendo {subiendo}…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" /> Agregar fotos
                </>
              )}
            </Button>
          </div>
        )}
      </div>

      {/* Chips de filtro */}
      {fotos.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {chips.map((c) => (
            <button
              key={String(c.valor)}
              type="button"
              onClick={() => setFiltro(c.valor)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs transition',
                filtro === c.valor
                  ? 'border-adelante-primary bg-adelante-primary/10 text-foreground'
                  : 'border-zinc-200 text-muted-foreground hover:bg-zinc-50',
              )}
            >
              {c.label}{' '}
              <span className="ml-0.5 tabular-nums text-[10px] text-muted-foreground">
                ({c.count})
              </span>
            </button>
          ))}
        </div>
      )}

      {errorBackend && <p className="text-sm text-destructive">{errorBackend}</p>}

      {consulta.isLoading ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {['a', 'b', 'c', 'd'].map((k) => (
            <div key={k} className="aspect-square animate-pulse rounded-md bg-muted" />
          ))}
        </div>
      ) : fotos.length === 0 ? (
        <div className="flex flex-col items-center gap-1 rounded-md border border-dashed py-8 text-sm text-muted-foreground">
          <ImageOff className="h-6 w-6" />
          Sin fotos.
          {puedeEscribir && ' Agregá una con el botón de arriba (se comprime automáticamente).'}
        </div>
      ) : fotosFiltradas.length === 0 ? (
        <div className="rounded-md border border-dashed py-6 text-center text-sm text-muted-foreground">
          No hay fotos en este filtro.
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-6">
          {fotosFiltradas.map((f) => {
            const badge =
              f.id_ensayo === null ? null : (labelEnsayo.get(f.id_ensayo) ?? `${f.id_ensayo}`);
            return (
              <div
                key={f.id}
                className="group relative aspect-square overflow-hidden rounded-md border"
              >
                <button
                  type="button"
                  onClick={() => setVerFoto(f)}
                  className="h-full w-full"
                  title="Ver foto"
                >
                  <img
                    src={f.url}
                    alt={f.nombre_original ?? 'Foto de muestra'}
                    loading="lazy"
                    className="h-full w-full object-cover transition group-hover:opacity-90"
                  />
                </button>
                {badge && (
                  <span className="pointer-events-none absolute bottom-1 left-1 rounded-md bg-black/55 px-1.5 py-0.5 text-[10px] font-medium text-white">
                    {badge}
                  </span>
                )}
                {puedeEscribir && (
                  <button
                    type="button"
                    onClick={() => {
                      if (confirm('¿Eliminar esta foto?')) {
                        eliminar.mutate({ id: f.id, idMuestra });
                      }
                    }}
                    disabled={eliminar.isPending}
                    className="absolute right-1 top-1 rounded-md bg-black/55 p-1 text-white opacity-0 transition hover:bg-red-600 group-hover:opacity-100"
                    title="Eliminar foto"
                    aria-label="Eliminar foto"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Visor a tamaño completo + reasignación */}
      <Dialog open={verFoto !== null} onOpenChange={(o) => !o && setVerFoto(null)}>
        <DialogContent className="max-w-3xl p-2">
          {verFoto && (
            <div className="space-y-2">
              <img
                src={verFoto.url}
                alt="Foto de muestra"
                className="max-h-[75vh] w-full rounded-md object-contain"
              />
              {puedeEscribir && (
                <div className="flex items-center gap-2 px-1 pb-1">
                  <span className="text-xs text-muted-foreground">Asignar a:</span>
                  <Select
                    value={ensayoAAsignacion(verFoto.id_ensayo)}
                    onValueChange={(v) => {
                      const idEnsayo = asignacionAEnsayo(v as AsignacionUI);
                      actualizar.mutate(
                        { id: verFoto.id, idMuestra, id_ensayo: idEnsayo },
                        {
                          onSuccess: (r) => {
                            if (r.data) setVerFoto(r.data);
                          },
                        },
                      );
                    }}
                    disabled={actualizar.isPending}
                  >
                    <SelectTrigger className="h-8 w-[160px] text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={SENTINEL_GENERAL}>General</SelectItem>
                      {ensayosOrden.map((e) => (
                        <SelectItem key={String(e.id)} value={String(e.id)}>
                          {e.edad_dias} días
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
